// src/utils/wingsUtility.jsx - Wings tracking and leaderboard
// Uses dedicated 'wings' collection
import { doc, setDoc, updateDoc, getDoc, collection, query, orderBy, limit, getDocs, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../components/firebase';
import { containsBadWords, getSafeLeaderboardName, logInappropriateName } from './badWordsFilter';

const DAILY_WING_LIMIT = 10;

export const ADMIN_EMAILS = [
    'pilothelper1903@gmail.com',
    'test23@gmail.com',
];

// Get today's date string in YYYY-MM-DD format (Eastern Time)
export const getTodayKey = () => {
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
    const parts = formatter.formatToParts(new Date());
    const year = parts.find(p => p.type === 'year').value;
    const month = parts.find(p => p.type === 'month').value;
    const day = parts.find(p => p.type === 'day').value;
    return `${year}-${month}-${day}`;
};

// Get wings data for a user
export const getWingsData = async (userId) => {
    try {
        const userDocRef = doc(db, 'wings', userId);
        const docSnap = await getDoc(userDocRef);

        if (docSnap.exists()) {
            return docSnap.data();
        }
        return null;
    } catch (error) {
        console.error('Error getting wings data:', error);
        return null;
    }
};

// Initialize wings document for a user if it doesn't exist
export const initializeWingsData = async (userId, userInfo) => {
    try {
        const userDocRef = doc(db, 'wings', userId);
        const docSnap = await getDoc(userDocRef);

        const displayName = userInfo?.displayName || 'Unknown User';
        const userEmail = userInfo?.email || '';

        // Check for bad words and log if found
        if (containsBadWords(displayName)) {
            logInappropriateName(userId, displayName, userEmail);
        }

        if (!docSnap.exists()) {
            const initialData = {
                totalWings: 0,
                dailyClaims: {},
                userName: displayName,
                userEmail: userEmail,
                createdAt: new Date().toISOString(),
                lastUpdated: new Date().toISOString()
            };
            await setDoc(userDocRef, initialData);
            return initialData;
        }

        // Update user name if it changed
        const existingData = docSnap.data();
        if (userInfo && existingData.userName !== displayName) {
            // Check for bad words in updated name too
            if (containsBadWords(displayName)) {
                logInappropriateName(userId, displayName, userEmail);
            }
            
            await setDoc(userDocRef, {
                userName: displayName,
                userEmail: userEmail,
                lastUpdated: new Date().toISOString()
            }, { merge: true });
            existingData.userName = displayName;
        }

        return existingData;
    } catch (error) {
        console.error('Error initializing wings data:', error);
        return null;
    }
};

// Check how many wings claimed today
export const getWingsClaimedToday = (wingsData) => {
    if (!wingsData || !wingsData.dailyClaims) return 0;
    const todayKey = getTodayKey();
    return wingsData.dailyClaims[todayKey] || 0;
};

// Claim a wing - returns { success, message, totalWings, claimedToday }
export const claimWing = async (userId, userInfo) => {
    try {
        const userDocRef = doc(db, 'wings', userId);

        // Initialize if needed
        let wingsData = await initializeWingsData(userId, userInfo);
        if (!wingsData) {
            return { success: false, message: 'Failed to load wings data. Please try again.' };
        }

        // Re-read to get latest
        const docSnap = await getDoc(userDocRef);
        wingsData = docSnap.data();

        const todayKey = getTodayKey();
        const claimedToday = (wingsData.dailyClaims && wingsData.dailyClaims[todayKey]) || 0;

        if (claimedToday >= DAILY_WING_LIMIT) {
            return {
                success: false,
                message: 'Daily limit reached. You can earn up to 10 Wings per day. Check back in 24 hours.',
                totalWings: wingsData.totalWings,
                claimedToday: claimedToday
            };
        }

        const newTotal = (wingsData.totalWings || 0) + 1;
        const newDailyCount = claimedToday + 1;

        const updateData = {
            totalWings: newTotal,
            [`dailyClaims.${todayKey}`]: newDailyCount,
            userName: userInfo?.displayName || wingsData.userName || 'Unknown User',
            userEmail: userInfo?.email || wingsData.userEmail || '',
            lastUpdated: new Date().toISOString()
        };

        await updateDoc(userDocRef, updateData);

        return {
            success: true,
            message: 'Wing claimed!',
            totalWings: newTotal,
            claimedToday: newDailyCount
        };
    } catch (error) {
        console.error('Error claiming wing:', error);
        return { success: false, message: 'Failed to claim wing. Please try again.' };
    }
};

const ADMIN_CONFIG_REF = () => doc(db, 'wings', 'adminConfig');

// Get the list of hidden user IDs from the admin config document
const getHiddenUsers = async () => {
    try {
        const configSnap = await getDoc(ADMIN_CONFIG_REF());
        if (configSnap.exists()) {
            return configSnap.data().hiddenUsers || [];
        }
        return [];
    } catch (error) {
        console.error('Error getting hidden users:', error);
        return [];
    }
};

// Hide a user from the leaderboard (adds userId to the hiddenUsers array)
// NOTE: This operation requires admin permissions enforced by Firestore rules
export const hideFromLeaderboard = async (userId) => {
    try {
        const configRef = ADMIN_CONFIG_REF();
        const configSnap = await getDoc(configRef);
        if (!configSnap.exists()) {
            await setDoc(configRef, { hiddenUsers: [userId] });
        } else {
            await updateDoc(configRef, { hiddenUsers: arrayUnion(userId) });
        }
        return { success: true };
    } catch (error) {
        console.error('Error hiding user from leaderboard:', error);
        
        // Check if this is a permission denied error
        if (error.code === 'permission-denied') {
            return { 
                success: false, 
                message: 'Permission denied. Only admins can hide users from leaderboard.',
                code: 'permission-denied'
            };
        }
        
        return { 
            success: false, 
            message: 'Failed to hide user from leaderboard. Please try again.'
        };
    }
};

// Unhide a user from the leaderboard (removes userId from the hiddenUsers array)
// NOTE: This operation requires admin permissions enforced by Firestore rules
export const unhideFromLeaderboard = async (userId) => {
    try {
        await updateDoc(ADMIN_CONFIG_REF(), { hiddenUsers: arrayRemove(userId) });
        return { success: true };
    } catch (error) {
        console.error('Error unhiding user from leaderboard:', error);
        
        // Check if this is a permission denied error
        if (error.code === 'permission-denied') {
            return { 
                success: false, 
                message: 'Permission denied. Only admins can unhide users from leaderboard.',
                code: 'permission-denied'
            };
        }
        
        return { 
            success: false, 
            message: 'Failed to unhide user from leaderboard. Please try again.'
        };
    }
};

// Get leaderboard - top 15 users by total wings
// When includeHidden is true, returns all users (with hidden flag) for admin management
export const getLeaderboard = async (includeHidden = false) => {
    try {
        const wingsRef = collection(db, 'wings');
        const q = query(wingsRef, orderBy('totalWings', 'desc'), limit(50));
        const [querySnapshot, hiddenUsers] = await Promise.all([getDocs(q), getHiddenUsers()]);

        const leaderboard = [];
        querySnapshot.forEach((docSnap) => {
            if (docSnap.id === 'adminConfig') return;
            const data = docSnap.data();
            if (data.totalWings > 0) {
                // Check for bad words - soft block by showing as Anonymous
                const safeName = getSafeLeaderboardName(data.userName);
                const hidden = hiddenUsers.includes(docSnap.id) || data.hiddenFromLeaderboard === true;
                if (!includeHidden && hidden) return;
                const displayName = formatLeaderboardName(safeName);
                
                const fullName = data.userName || 'Unknown User';
                leaderboard.push({
                    id: docSnap.id,
                    name: displayName,
                    fullName,
                    wings: data.totalWings,
                    email: data.userEmail || '',
                    hidden
                });
            }
        });

        // Cap visible results to 15 for non-admin view
        if (!includeHidden) {
            return leaderboard.slice(0, 15);
        }

        return leaderboard;
    } catch (error) {
        console.error('Error getting leaderboard:', error);
        return [];
    }
};

// Format name for leaderboard: "First Name Last Initial."
const formatLeaderboardName = (fullName) => {
    if (!fullName || fullName === 'Unknown User') return 'Anonymous';

    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) {
        return parts[0];
    }

    const firstName = parts[0];
    const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
    return `${firstName} ${lastInitial}.`;
};
